import TitleHeader from "@/components/TitleHeader";
import { Box, Flex, Text, Image, useDisclosure } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import ACHIcon from "./assets/ach.png";
import { useState } from "react";
import { shortenAddress } from "@/utils";
import WithdrawAmountModal from "./WithdrawAmountModal";
import WithdrawButton from "./WithdrawButton";
import { createBundler } from "@/constants/bundler";
import { useUserInfo } from "@/context/UserInfo";
import capsule from "@/constants/capsule";
import { parseAmount } from "@/utils/formatBalance";
import { usdcInfo } from "@/constants/tokens";
import { encodeFunctionData } from "viem";
import { createWithdrawOrder, Order } from "@/api/order";
import WithdrawComplete from "./WithdrawComplete";
import WithdrawLoading from "./WithdrawLoading";
import {
  createLiquidationAddress,
  ExternalAccount,
  LiquidationAddress,
} from "@/api/bridge";

const paymothds = [
  {
    icon: ACHIcon,
    name: "ACH",
    time: "1-3 business days",
    value: "ach",
    fee: "0.5",
  },
  {
    icon: ACHIcon,
    name: "Same Day ACH",
    time: "Within 1 business day",
    value: "ach_same_day",
    fee: "1",
  },
];

const WithdrawDetail = ({
  onBack,
  withdrawMethod,
  withdrawAddress,
  selectBank,
  liquidationList,
  handleGetLiquidationAddress,
}: {
  onBack: () => void;
  withdrawMethod: string;
  withdrawAddress: string;
  selectBank: ExternalAccount | null;
  liquidationList: LiquidationAddress[];
  handleGetLiquidationAddress: () => void;
}) => {
  const navigate = useNavigate();
  const { usdcBalance, smartAccount, address, senderAddress, bridgeCustomer } =
    useUserInfo();
  const [payActiveIndex, setPayActiveIndex] = useState(0);
  const [payStatus, setPayStatus] = useState("");
  const [withdrawOrder, setWithdrawOrder] = useState<Order | null>(null);
  const [amount, setAmount] = useState("0");
  const {
    isOpen: isOpenComplete,
    onClose: onCloseComplete,
    onOpen: onOpenComplete,
  } = useDisclosure({});

  const {
    isOpen: isAmountOpen,
    onOpen: onAmountOpen,
    onClose: onAmountClose,
  } = useDisclosure({ defaultIsOpen: true });

  const handleWithdraw = async () => {
    if (!amount || amount === "0") {
      return;
    }

    if (!selectBank) {
      return;
    }

    setPayStatus("loading");

    let toAddress = "";
    if (withdrawMethod === "bank") {
      const paymothd = paymothds[payActiveIndex].value;
      const liquidationAddress = liquidationList.find(
        (item) =>
          item.external_account_id === selectBank!.id &&
          item.destination_payment_rail === paymothd
      );

      if (liquidationAddress) {
        toAddress = liquidationAddress.address;
      } else {
        const payload = {
          external_account_id: selectBank!.id,
          chain: "base",
          currency: "usdc",
          destination_payment_rail: "ach",
          destination_currency: "usd",
        };
        try {
          const res = await createLiquidationAddress(payload);
          console.log(res, "res");
          handleGetLiquidationAddress();
          toAddress = res.address;
        } catch (error) {
          console.error("创建失败:", error);
          setPayStatus("error");
          return;
        }
      }
    } else {
      toAddress = withdrawAddress;
    }

    try {
      const isActive = await capsule.isSessionActive();
      if (!isActive) {
        await capsule.setWallets({});
        await capsule.refreshSession(false);
        return;
      }
    } catch (error) {
      console.error("Session check failed:", error);
      setPayStatus("error");
      return;
    }

    const withdrawAmount = parseAmount(amount, usdcInfo.decimals);

    const transferCallData = await smartAccount!.encodeCalls([
      {
        to: usdcInfo.address,
        value: 0n,
        data: encodeFunctionData({
          abi: [
            {
              type: "function",
              name: "transfer",
              inputs: [
                {
                  name: "to",
                  type: "address",
                  internalType: "address",
                },
                {
                  name: "amount",
                  type: "uint256",
                  internalType: "uint256",
                },
              ],
              outputs: [],
              stateMutability: "nonpayable",
            },
          ],
          args: [toAddress as `0x${string}`, BigInt(withdrawAmount)],
        }),
      },
    ]);

    try {
      const bundler = await createBundler(smartAccount!);
      const tx = await bundler.sendTransaction(transferCallData);
      const order = await createWithdrawOrder({
        spendAmount: withdrawAmount,
        recipientAmount: withdrawAmount,
        opUserHash: tx.userOpHash,
        sender: senderAddress,
        recipient: toAddress,
      });
      setWithdrawOrder(order);
      onOpenComplete();

      setPayStatus("complete");
    } catch (error) {
      console.error("发送失败:", error);
      setPayStatus("error");
    }
  };
  return (
    <Flex sx={{}} flexDir={"column"} alignItems={"center"}>
      <Box sx={{ padding: "20px", width: "100%" }}>
        <TitleHeader title="Withdraw" color="#D8762B" onBack={onBack} />
      </Box>
      <Text
        sx={{
          color: "#848484",
          textAlign: "center",
          fontSize: "16px",
          fontWeight: 700,
          marginTop: "12px",
        }}
      >
        Withdraw Amount
      </Text>
      <Text
        sx={{
          color: "#333",
          textAlign: "center",
          fontFamily: "DIN-Black",
          fontSize: "50px",
          fontWeight: 900,
          marginTop: "12px",
        }}
        onClick={onAmountOpen}
      >
        {amount}
      </Text>
      <Text
        sx={{
          color: "#333",
          textAlign: "center",
          fontFamily: "DIN",
          fontSize: "20px",
          fontWeight: 700,
        }}
      >
        USDC
      </Text>
      <Box
        sx={{
          height: "120px",
          marginTop: "20px",
          width: "100%",
        }}
      >
        {withdrawMethod === "bank" &&
          paymothds.map((item, index) => (
            <Flex
              key={index}
              alignItems={"center"}
              sx={{
                height: "45px",
                background:
                  payActiveIndex === index ? "#FFD7AC" : "transparent",
                padding: "0 20px",
              }}
              onClick={() => {
                setPayActiveIndex(index);
              }}
            >
              <Image src={item.icon} sx={{ width: "30px" }}></Image>
              <Text
                sx={{
                  color: "#333",
                  fontFamily: "DIN",
                  fontSize: "16px",
                  fontStyle: "normal",
                  fontWeight: 700,
                  marginLeft: "12px",
                }}
              >
                {item.name}
              </Text>
              <Text
                sx={{
                  color: "#333",
                  textAlign: "right",
                  fontFamily: "DIN",
                  fontSize: "14px",
                  fontStyle: "normal",
                  fontWeight: 500,
                  marginLeft: "auto",
                }}
              >
                {item.time}
              </Text>
            </Flex>
          ))}
      </Box>
      <Box
        sx={{
          borderTop: "1px dashed #E5E5E5",
          width: "100%",
          padding: "15px 20px 0",
        }}
      >
        <Flex justify={"space-between"}>
          <Text
            sx={{
              color: "#848484",
              fontFamily: "DIN-Black",
              fontSize: "16px",
              fontWeight: 900,
            }}
          >
            Fee:
          </Text>
          <Text
            sx={{
              color: "#333",
              fontFamily: "DIN-Black",
              fontSize: "16px",
              fontWeight: 900,
            }}
          >
            {withdrawMethod === "bank" ? paymothds[payActiveIndex].fee : "0"}
            <span style={{ fontSize: "14px", fontWeight: 700 }}>USD</span>
          </Text>
        </Flex>
        <Flex justify={"space-between"} sx={{ paddingTop: "15px" }}>
          <Text
            sx={{
              color: "#848484",
              fontFamily: "DIN-Black",
              fontSize: "16px",
              fontWeight: 900,
            }}
          >
            Total withdraw:
          </Text>
          <Text
            sx={{
              color: "#333",
              fontFamily: "DIN-Black",
              fontSize: "16px",
              fontWeight: 900,
            }}
          >
            {amount}{" "}
            <span style={{ fontSize: "14px", fontWeight: 700 }}>USD</span>
          </Text>
        </Flex>
        <Flex justify={"space-between"} sx={{ paddingTop: "15px" }}>
          <Text
            sx={{
              color: "#848484",
              fontFamily: "DIN-Black",
              fontSize: "16px",
              fontWeight: 900,
            }}
          >
            Withdrawal to:
          </Text>
          {withdrawMethod === "bank" && (
            <Text
              sx={{
                color: "#333",
                fontFamily: "DIN-Black",
                fontSize: "16px",
                fontWeight: 900,
              }}
            >
              {selectBank?.bank_name} ... {selectBank?.last_4}
            </Text>
          )}
          {withdrawMethod === "wallet" && (
            <Text
              sx={{
                color: "#333",
                fontFamily: "DIN-Black",
                fontSize: "16px",
                fontWeight: 900,
              }}
            >
              {shortenAddress(withdrawAddress)}
            </Text>
          )}
        </Flex>
      </Box>
      <WithdrawButton
        disabled={amount === "0"}
        onConfirm={handleWithdraw}
      ></WithdrawButton>
      <WithdrawLoading payStatus={payStatus} />
      <WithdrawComplete
        order={withdrawOrder}
        isOpen={isOpenComplete}
        onClose={onCloseComplete}
      ></WithdrawComplete>
      <WithdrawAmountModal
        isOpen={isAmountOpen}
        onClose={onAmountClose}
        onValueChange={(value) => {
          setAmount(value);
        }}
      ></WithdrawAmountModal>
    </Flex>
  );
};

export default WithdrawDetail;
